import React, { useEffect, useMemo, useState } from 'react';
import { Collapse, Empty, Spin, Select } from 'antd';
import { HighMedLowColor } from '../components/HighMedLowColor';
import { CaretRightOutlined } from '@ant-design/icons';
import TableView from './TableView';
import styled, {
  ScoreExplainablityContainer,
  LoadingWrapper,
  PreventionContainer
} from './styled';
import PanelExtraTemplate from '../components/PanelExtraTemplate';
import SwitchCumToggal from '../../common/components/SwitchCumToggal';
import hardRuleFeather from '../../../assets/hardRuleFeather.svg';
import nonHardRule from '../../../assets/nonHardRule.svg';
import PreventionTable from './preventionTable';
import { groupingKeyDisplayName } from './groupingKeyLabel';
// FIN-12201: ScoreComputationCard (Total/Aggregated-contributions block) hidden
// on both alert-detail surfaces per product; component retained for future use.
import style from './index.scss';
import { hasAttributeValue } from '../../../util';
import { getSelectedResources } from '../../../util/resourceManager';

import { cloneDeep } from '../../../util/lodashlike';
const { plrtb, styleCustomPanel, styleHighMedLow, panelHeading } = styled;
const { Panel } = Collapse;
const { trmResources } = getSelectedResources();

const ScoreExplanability = ({
  authentication,
  scoreExplainability,
  apiActions,
  scoreExplainabilityPrevention,
  alertScoreExplanation,
  getAMLSCommonAlertPartyIdentity,
  alertPartyIdentity,
  unifiedAlerts,
  isPrevention,
  trmActions,
  fieldsMetaData,
  alertLevelConfigs,
  ...props
}) => {
  const {
    alertDetails: { data }
  } = unifiedAlerts;
  const {
    getScoreExplainablity,
    getAmlsCommonPreventionScoreExplainablity,
    getAlertScoreExplanation,
    fetchTypologiesActions,
    fetchQueryBuilderMetadata,
    fetchAMLSAlertLevel
  } = apiActions;
  const [newDataSource, setNewDataSource] = useState([]);
  const [switchStatus, setSwitchStatus] = useState(true);
  const { alertId, isLoading } = props;

  const [rules, setRules] = useState([]);
  const [dropdownRules, setDropdownRules] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState({
    rule: '',
    role: ''
  });

  const parsedRuleContributions = useMemo(() => {
    if (!alertScoreExplanation?.ruleContributions) return null;
    try {
      return JSON.parse(alertScoreExplanation.ruleContributions);
    } catch {
      return null;
    }
  }, [alertScoreExplanation]);

  const preventionResourceAccess = hasAttributeValue(
    authentication,
    trmResources.APP_3_RESOURCE_UI_RUN_INSTANCE,
    '24'
  );

  useEffect(() => {
    // FIN-12833 hardening — an absent alertId fired GET /alerts/undefined/... here.
    if (alertId == null || alertId === '') return;
    if (isPrevention) {
      getAmlsCommonPreventionScoreExplainablity({ alertId: alertId });
      getAlertScoreExplanation({ alertId: alertId });
    } else {
      getScoreExplainablity({ alertId });
    }
  }, [alertId]);

  useEffect(() => {
    if (preventionResourceAccess && !trmActions?.data?.length) {
      fetchTypologiesActions();
    }
  }, [preventionResourceAccess]);

  useEffect(() => {
    // FIN-12133 — the Action value is colour-coded by its configured severity's
    // level colour (alert_level_details). Run-Instance detail doesn't always load
    // these configs, so fetch them here when absent (the global saga is idempotent).
    if (
      isPrevention &&
      typeof fetchAMLSAlertLevel === 'function' &&
      !alertLevelConfigs?.length
    ) {
      fetchAMLSAlertLevel();
    }
  }, [isPrevention]);

  useEffect(() => {
    // FIN-11960 — ensure the query-builder field metadata is loaded so the
    // Group By column renders the same human-readable field names as the Rule
    // configuration screen (state.amlsCommon.queryFields.data.fields).
    if (
      isPrevention &&
      typeof fetchQueryBuilderMetadata === 'function' &&
      Object.keys(fieldsMetaData || {}).length === 0
    ) {
      fetchQueryBuilderMetadata();
    }
  }, [isPrevention]);

  useEffect(() => {
    const dataSource =
      switchStatus &&
      cloneDeep(scoreExplainability)?.map((records) => {
        records.riskIndicators = records?.riskIndicators.filter(
          ({ importance }) => !!importance
        );
        return records;
      });

    setNewDataSource(switchStatus ? dataSource : scoreExplainability);
  }, [scoreExplainability, switchStatus]);

  useEffect(() => {
    // The rules dropdown options constructed from score explainability data.
    // So set the options when rule and role is all otherwise do nothing.
    if (
      !selectedFilter.rule &&
      !selectedFilter.role &&
      scoreExplainabilityPrevention
    ) {
      const uniqueEntityRules = new Set();
      Object.keys(scoreExplainabilityPrevention).forEach((typologyName) => {
        const typologiesForName = scoreExplainabilityPrevention[typologyName];
        typologiesForName.forEach((typology) => {
          const { triggerName } = typology;
          if (!uniqueEntityRules.has(triggerName)) {
            uniqueEntityRules.add(triggerName);
          }
        });
      });
      setRules(Array.from(uniqueEntityRules));
    }
  }, [scoreExplainabilityPrevention]);

  const setRolesState = (state) => {
    // The roles dropdown options constructed from score explainability data.
    // So set the options when role and rule is all otherwise do nothing.
    if (!selectedFilter.role && !selectedFilter.rule) {
      const uniqueState = Array.from(new Set(state));
      setDropdownRules(uniqueState);
    }
  };

  const filterChange = (filters) => {
    const params = { alertId };

    if (filters.role) {
      params['entityRole'] = filters.role;
    }

    if (filters.rule) {
      params['ruleName'] = filters.rule;
    }

    setSelectedFilter(filters);
    getAmlsCommonPreventionScoreExplainablity(params);
  };

  const ruleOptions = useMemo(
    () => [
      { value: '', label: 'All' }, // Add the 'All' option
      ...rules.map((rule) => ({ value: rule, label: rule })) // Add other rule options
    ],
    [rules]
  );

  const roleOptions = useMemo(
    () => [
      { value: '', label: 'All' }, // Add the 'All' option
      // FIN-11960 — show the human-readable field label (e.g. "Sender Identifier")
      // for the grouping key, in lock-step with the column + the Rule config
      // screen. The value sent to the API stays the raw key.
      ...dropdownRules.map((role) => ({
        value: role,
        label: groupingKeyDisplayName(role, fieldsMetaData)
      }))
    ],
    [dropdownRules, fieldsMetaData]
  );

  return isPrevention ? (
    <ScoreExplainablityContainer>
      <PreventionContainer>
        <div className={style.PreventionContainer}>
          <header
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingBottom: '10px'
            }}>
            {/* <b className="mb-3"> Details </b> */}
            <div
              style={{
                display: 'flex',
                gap: '30px',
                width: '100%',
                justifyContent: 'flex-end'
              }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label>{`Group By`}</label>
                <Select
                  style={{ width: 350 }}
                  defaultValue=""
                  onChange={(role) => filterChange({ ...selectedFilter, role })}
                  options={roleOptions}
                  placeholder="Please Select Grouping"
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label>{`Variant Name`}</label>
                <Select
                  style={{ width: 350 }}
                  defaultValue=""
                  onChange={(rule) => filterChange({ ...selectedFilter, rule })}
                  options={ruleOptions}
                  placeholder="Please Select Variant"
                />
              </div>
            </div>
          </header>
          {/* FIN-12201: the Score Computation card (Total / Aggregated
              contributions) exposed the raw internal MAX(...) formula. Hidden
              on both surfaces (Run Instance + CM alert detail) as confirmed
              with the product team. Per-rule rows below are unaffected. */}
          <PreventionTable
            scoreExplainabilityData={scoreExplainabilityPrevention}
            ruleContributions={parsedRuleContributions}
            setRolesState={setRolesState}
            alertInfo={data}
            trmActions={trmActions}
            selectedFilter={selectedFilter}
            isLoading={isLoading}
            fieldsMetaData={fieldsMetaData}
            alertLevelConfigs={alertLevelConfigs}
          />
          {/* FIN-13286: the same action (e.g. SOFT STOP) can render different
              scores on different rows, because a variant may carry a per-rule
              Scoring Override that supersedes the Action Catalog's default
              weight. That is by design — the table shows each rule as it was
              actually scored — but with no signal it reads as two conflicting
              values for one action. Requested by the PO on the ticket. */}
          <p className={style.ScoreDisclaimer}>
            Actions and scores are determined independently by each variant&apos;s
            configuration. The same action may have different configured weights
            across variants and therefore result in different scores.
          </p>
        </div>
      </PreventionContainer>
    </ScoreExplainablityContainer>
  ) : (
    <ScoreExplainablityContainer>
      <header style={plrtb}>
        <b className="mb-3"> Score Explanation </b>

        <div style={styleHighMedLow}>
          <SwitchCumToggal
            onChange={(status) => setSwitchStatus(status)}
            defaultValue={switchStatus}
            onText="Show non-contributing indicators"
            offText="Hide non-contributing indicators"
          />
          <HighMedLowColor />
        </div>
      </header>
      <Collapse
        style={{ backgroundColor: '#fff' }}
        defaultActiveKey={['1']}
        bordered={false}
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}>
        {newDataSource?.length ? (
          newDataSource.map((item, index) => (
            <Panel
              header={
                <span style={panelHeading}>
                  <img src={item.isHardRule ? hardRuleFeather : nonHardRule} />{' '}
                  &nbsp; {item?.name}
                </span>
              }
              key={index + 1}
              extra={PanelExtraTemplate(item)}
              style={styleCustomPanel}>
              <TableView
                tableData={item?.riskIndicators}
                enableClusterMean={item.enableClusterMean || false}
                mandatoryScoringCondition={item.conditionForMandatoryScoring}
                authentication={authentication}
              />
            </Panel>
          ))
        ) : (
          <LoadingWrapper>
            {isLoading ? (
              <Spin size="large" />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </LoadingWrapper>
        )}
      </Collapse>
    </ScoreExplainablityContainer>
  );
};

export default ScoreExplanability;
